import axios from 'axios';
import { API_BASE_URL, AUTH_ENDPOINTS } from '../config/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000 // 10 seconds timeout
});

// Add request interceptor to handle authentication
api.interceptors.request.use(
  async (config) => {
    // Skip auth if specified in request config
    if (config.skipAuth) {
      return config;
    }
    
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Only try to refresh the token if:
    // 1. Error is 401 (Unauthorized)
    // 2. We haven't tried to refresh the token yet
    // 3. The request wasn't to the login or refresh endpoint
    if (
      error.response?.status === 401 && 
      !originalRequest._retry &&
      !originalRequest.url?.includes(AUTH_ENDPOINTS.LOGIN) &&
      !originalRequest.url?.includes(AUTH_ENDPOINTS.REFRESH)
    ) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          // No refresh token, need to login again
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Try to refresh the token
        const response = await axios.post(
          `${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`,
          { refresh_token: refreshToken },
          { 
            headers: { 'Content-Type': 'application/json' },
            skipAuth: true 
          }
        );
        
        const { access_token, refresh_token } = response.data;
        
        // Save new tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Update authorization header for original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear storage and redirect to login
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Handle specific error codes
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.config.url);
    } else if (error.response?.status === 403) {
      console.error('Permission denied for resource:', error.config.url);
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.status, error.config.url);
    }
    
    return Promise.reject(error);
  }
);

// API methods
const apiClient = {
  get: async (url, params = {}, skipAuth = false) => {
    try {
      //console.log(`Making GET request to: ${url}`);
      const response = await api.get(url, { 
        params,
        skipAuth 
      });
      return response.data;
    } catch (error) {
      console.error(`Error in GET request to ${url}:`, error);
      throw error;
    }
  },
  
  post: async (url, data = {}, skipAuth = false) => {
    try {
      //console.log(`Making POST request to: ${url}`);
      const response = await api.post(url, data, { skipAuth });
      return response.data;
    } catch (error) {
      console.error(`Error in POST request to ${url}:`, error);
      throw error;
    }
  },
  
  put: async (url, data = {}, skipAuth = false) => {
    try {
      //console.log(`Making PUT request to: ${url}`);
      const response = await api.put(url, data, { skipAuth });
      return response.data;
    } catch (error) {
      console.error(`Error in PUT request to ${url}:`, error);
      throw error;
    }
  },
  
  delete: async (url, skipAuth = false) => {
    try {
      //console.log(`Making DELETE request to: ${url}`);
      const response = await api.delete(url, { skipAuth });
      return response.data;
    } catch (error) {
      console.error(`Error in DELETE request to ${url}:`, error);
      throw error;
    }
  }
};

export default apiClient; 